import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchLayoutBootstrap, type LayoutBootstrap } from './bootstrapApi';

const emptyBootstrap: LayoutBootstrap = { media: {}, pageSeo: {}, siteSettings: {}, customPageSlugs: [], blogSlugs: [] };

interface BootstrapState extends LayoutBootstrap {
  /** False until the initial fetch settles (success or failure) — lets
   * consumers like the ":slug" catch-all route (CustomPage.tsx) wait for
   * customPageSlugs to be trustworthy before deciding a path is a 404. */
  ready: boolean;
}

const BootstrapContext = createContext<BootstrapState>({ ...emptyBootstrap, ready: false });

// A single failed/aborted attempt (a burst of near-simultaneous requests
// under crawler-style load has been observed to occasionally fail even
// though the endpoint itself is healthy -- see BlogPost.tsx/CustomPage.tsx)
// must never be treated as "confirmed this slug doesn't exist": that's what
// previously got a real, published blog post excluded from Google as a
// false 404/noindex. A network failure means "unknown," not "not found," so
// retry a couple of times with backoff before accepting defeat.
const RETRY_DELAYS_MS = [600, 1500];

async function fetchLayoutBootstrapWithRetry(): Promise<LayoutBootstrap> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchLayoutBootstrap();
    } catch (err) {
      if (attempt >= RETRY_DELAYS_MS.length) throw err;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  }
}

/** Fetches the media/pageSeo/siteSettings/customPageSlugs needed on every
 * guest page in a single request, instead of several separate ones.
 * Mounted once at the Layout level so it only runs once per session (SPA
 * navigation doesn't remount Layout). */
export function BootstrapProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<BootstrapState>({ ...emptyBootstrap, ready: false });

  useEffect(() => {
    let cancelled = false;
    fetchLayoutBootstrapWithRetry()
      .then((result) => {
        if (!cancelled) setData({ ...result, ready: true });
      })
      .catch(() => {
        if (!cancelled) setData((d) => ({ ...d, ready: true }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <BootstrapContext.Provider value={data}>{children}</BootstrapContext.Provider>;
}

export function useBootstrap(): BootstrapState {
  return useContext(BootstrapContext);
}
